import { Checkbox, HStack, Stack, Text, VStack } from 'native-base';
import { Controller } from 'react-hook-form';
import TextArea from 'ui/components/TextArea';
import Select from 'ui/components/v2/inputs/Select';
import TextInput from 'ui/components/v2/inputs/TextInput';
import LocationAutoComplete from 'ui/components/v2/LocationInputs/LocationAutoComplete';

import { useOffPlatformDefinitionFormController } from '~/pages/Authenticated/OffPlatformApplication/components/DefinitionForm/controller';
import { IOffPlatformDefinitionProps } from '~/pages/Authenticated/OffPlatformApplication/components/DefinitionForm/types';
import OffPlatformFormFooter from '~/pages/Authenticated/OffPlatformApplication/components/FormFooter';
import { GooglePlacesAutoCompleteAdapter } from '~/services/google/google-places-auto-auto-complete';

import { useEcosystemRefresh } from '~/hooks/useEcosystemRefresh';
import { OffPlatformLogInput } from '~/pages/Authenticated/OffPlatformApplication/components/OffPlatformLogInput';
import { CustomDatePicker } from 'ui/components/DateTimePickers/DatePicker';
import FormHeader from 'ui/components/FormBuilderV2/components/FormHeader';

const googlePlacesAutoComplete = GooglePlacesAutoCompleteAdapter.getSingleton();

export const OffPlatformDefinitionForm = (
  props: IOffPlatformDefinitionProps,
) => {
  const { causeOptions, isLoading } = props;

  const {
    control,
    measurementFields,
    onSubmitHandler,
    formState,
    onCancelHandler,
  } = useOffPlatformDefinitionFormController(props);

  useEcosystemRefresh({ enabled: true });

  return (
    <VStack w="full" space={4} p={{ base: 4, md: 6 }}>
      <FormHeader title="Activity details" subTitle="" />
      <HStack alignItems={'center'} space={6} maxW={{ base: '60%', lg: '40%' }}>
        <Text fontWeight={'500'} fontSize={'sm'} color={'gray.600'}>
          Date
        </Text>
        <CustomDatePicker
          inputProps={{
            height: 12,
            padding: 2,
          }}
          control={control}
          name="measurementDate"
          dateFormat="dd/MM/yyyy"
          maxDate={new Date()}
          showTimeSelect={false}
          errorMessage={formState.errors?.measurementDate?.message || ''}
        />
      </HStack>

      <TextInput.Controlled
        control={control}
        name="title"
        label="Title"
        placeholder="Title of the activity"
        errorMessage={formState.errors?.title?.message}
        isRequired
      />
      <Stack>
        <TextArea
          control={control}
          name="description"
          label="Description"
          fontSize={16}
          placeholder="Describe the activity (optional)."
          errorMessage={formState.errors?.description?.message}
        />
      </Stack>
      <TextInput.Controlled
        control={control}
        name="organizationName"
        label="Charity"
        placeholder="Enter the name of the charity"
        errorMessage={formState.errors?.organizationName?.message}
        isRequired
      />
      <Select.Controlled
        isMulti
        control={control}
        name="causeOptions"
        label="Causes"
        options={causeOptions}
        errorMessage={formState.errors?.causeOptions?.message}
        isLoading={!causeOptions.length}
        isClearable
        isRequired
      />
      <HStack
        direction={{ base: 'row', lg: 'row' }}
        space={6}
        w="full"
        alignItems={'center'}
        style={{ zIndex: 99 }}
      >
        <Stack w={{ base: '100%' }}>
          <Controller
            name="location"
            control={control}
            render={({ field: { onChange, value } }) => (
              <LocationAutoComplete
                label="Location"
                onChange={onChange}
                defaultValue={value}
                placeholder="Post code / Town"
                provider={googlePlacesAutoComplete}
                errorMessage={formState.errors?.location?.message?.toString()}
                height={'3.5rem'}
                isRequired
              />
            )}
          />
        </Stack>
        <Stack w="160px" h={'full'} marginTop={'32px'}>
          <Controller
            control={control}
            name="isRemote"
            render={({ field: { name, value, onChange } }) => (
              <Checkbox
                name={name}
                value="FROM_HOME"
                isChecked={value}
                onChange={onChange}
                rounded={'50%'}
              >
                <Text fontSize={'14px'} fontWeight={400}>
                  From home
                </Text>
              </Checkbox>
            )}
          />
        </Stack>
      </HStack>

      <VStack mt={10} mb={10} space={4}>
        <FormHeader
          title="Participants"
          subTitle="If registering for a group, fill in the information below."
        />
        <TextInput.Controlled
          control={control}
          name="numberOfParticipants"
          label="Number of participants"
          placeholder="Enter the number of participants"
          errorMessage={formState.errors?.numberOfParticipants?.message}
        />
        <VStack>
          <TextInput.Controlled
            control={control}
            name="nameofParticipants"
            label="Participants names"
            placeholder="Enter participants names"
            errorMessage={formState.errors?.nameofParticipants?.message}
            helpText="Enter name separated by comma. Ex: Name 1, Name 2, Name 3"
            showHelpTextOnMobile
          />
        </VStack>
      </VStack>

      <VStack space="4">
        <FormHeader
          title="How did you contribute?"
          subTitle="Fill in at least one unit."
        />
        {measurementFields.map(({ measurementUnit }, index) => (
          <OffPlatformLogInput
            control={control}
            key={index}
            index={index}
            measurementUnit={measurementUnit}
            errorMessage={
              formState.errors?.measurements?.[index]?.amount?.message || ''
            }
          />
        ))}
      </VStack>

      <OffPlatformFormFooter
        onAction={onSubmitHandler}
        onCancel={onCancelHandler}
        actionLabel="Log"
        isLoading={isLoading}
      />
    </VStack>
  );
};

export default OffPlatformDefinitionForm;
